<script setup>
import FormSection from "@/Components/FormSection.vue";
import AppLayout from "@/Layouts/AppLayout.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import InputLabel from "@/Components/InputLabel.vue";
import InputError from "@/Components/InputError.vue";
import TextInput from "@/Components/TextInput.vue";
import SelectInput from "@/Components/SelectInput.vue";
import { useForm, usePage } from "@inertiajs/inertia-vue3";
import { computed, ref, watch } from "vue";
import PhoneInput from "@/Components/PhoneInput.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import { useHelpers, useTenantTextRules } from "@/helpers";
import CompanyLicenseDocumentField from "@/Components/Admin/Company/CompanyLicenseDocumentField.vue";

const props = defineProps({
  carriers: Array,
  accountManagers: Array,
  companyClasses: Object,
  publicCompanyUpdateUrl: {
    type: String,
    default: null,
  },
  publicCompanyUpdateEnabled: {
    type: Boolean,
    default: false,
  },
  licenseDocument: {
    type: Object,
    default: null,
  },
});

const helpers = useHelpers();
const { resolveTextRule } = useTenantTextRules();
const currentUser = usePage().props.value.auth.user;
const companyProperty = computed(() => usePage().props.value.company);
const company = companyProperty.value;
console.log('Editing Company:', company);
watch(companyProperty, (value) => resetForm(value));

const form = useForm({
  _method: 'POST',
  photo: null,
  client: company.client,
  contact: company.contact,
  phone: company.phone,
  fax: company.fax,
  address: company.address,
  address2: company.address2,
  city: company.city,
  state: company.state,
  zip: company.zip,
  country: company.country,
  email: company.email,
  www: company.www,
  clientID: company.clientID,
  class: company.class || null,
  accountmanager: company.accountmanager,
  shipperno: company.shipperno,
  carrier: company.carrier,
  intranet: company.intranet,
  trucking_service: company.trucking_service,
  insurance_carrier_policy_number: company.insurance_carrier_policy_number,
  notes: company.notes,
  active: company.active || 'yes',
  has_notifications: (typeof company.has_notifications === 'undefined') ? true : company.has_notifications,
  logo: company.logo,
  client_code: company.client_code || null,
  license_document: null,
  remove_license_document: false,
});
const saving = ref(false);
const markedLicenseForRemoval = ref(false);
const saved = ref(false);
const publicLinkCopied = ref(false);
const photoPreview = ref(null);
const photoInput = ref(null);
const addressLabel = computed(() =>
  resolveTextRule('company.form.address', 'Address', 'admin.company.form.address')
);
const address2Label = computed(() =>
  resolveTextRule('company.form.address2', 'Address 2', 'admin.company.form.address2')
);
const stateLabel = computed(() =>
  resolveTextRule('company.form.state', 'State', 'admin.company.form.state')
);
const truckingServiceLabel = computed(() =>
  resolveTextRule(
    'company.form.trucking_service',
    'Trucking Service (If applicable)',
    'admin.company.form.trucking_service'
  )
);
const insuranceCarrierPolicyNumberLabel = computed(() =>
  resolveTextRule(
    'company.form.insurance_carrier_policy_number',
    'Insurance Carrier and Policy #',
    'admin.company.form.insurance_carrier_policy_number'
  )
);
const contactIsRequired = computed(() => !!company.ID || !props.publicCompanyUpdateEnabled);
const licenseDocumentState = computed(() => props.licenseDocument);

const resetLicenseDocumentFormState = () => {
  form.license_document = null;
  form.remove_license_document = false;
  markedLicenseForRemoval.value = false;
};
const saveForm = () => {
  saving.value = true;
  let endpoint = route('admin.companies.store');
  if (company.ID) {
    form._method = 'PUT';
    endpoint = route('admin.companies.update', { company: company.ID });
  }
  if (photoInput.value) {
    form.photo = photoInput.value.files[0];
  }

  const postOptions = {
    preserveScroll: true,
    onSuccess() {
      saved.value = true;
      setTimeout(() => saved.value = false, 3000);
      clearPhotoFileInput();
      resetLicenseDocumentFormState();
    },
    onError() {
      saving.value = false;
      saved.value = false;
      const element = document.getElementById('client')
      element.scrollIntoView();
    }
  };

  if (form.photo || form.license_document) {
    postOptions.forceFormData = true;
  }

  form.post(endpoint, postOptions);
  saving.value = false;
};

const onLicenseFileSelected = (file) => {
  form.license_document = file;
  form.remove_license_document = false;
  markedLicenseForRemoval.value = false;
};

const onLicenseRemove = () => {
  form.license_document = null;
  form.remove_license_document = true;
  markedLicenseForRemoval.value = true;
};

const onLicenseUndoRemove = () => {
  form.license_document = null;
  form.remove_license_document = false;
  markedLicenseForRemoval.value = false;
};

const resetForm = (clientData) => {
  if (Object.keys(usePage().props.value.errors).length > 0) {
    return;
  }

  photoPreview.value = null;
  form.photo = null;
  clearPhotoFileInput();
  company.profile_photo_url = clientData.profile_photo_url;
  company.ID = clientData.ID;
  company.client = clientData.client;
  company.users = clientData.users;
  Object.keys(form.data()).forEach(key => {
    form[key] = clientData[key];
  });
}

const selectNewPhoto = () => {
  photoInput.value.click();
};

const updatePhotoPreview = () => {
  const photo = photoInput.value.files[0];

  if (!photo) return;

  const reader = new FileReader();

  reader.onload = (e) => {
    photoPreview.value = e.target.result;
  };

  reader.readAsDataURL(photo);
};

const deletePhoto = () => {
  form.photo = null;
  form.logo = null;
  photoPreview.value = null;
  clearPhotoFileInput();
  saveForm();
};

const clearPhotoFileInput = () => {
  if (photoInput.value?.value) {
    photoInput.value.value = null;
  }
};

const goBack = () => {
  if (typeof history !== "undefined" && history.length > 1) history.back();
  else
    Inertia.get(
      route("admin.companies.active"),
      {},
      { preserveState: true, preserveScroll: true }
    );
};

const copyPublicCompanyUpdateLink = async () => {
  const text = props.publicCompanyUpdateUrl;
  if (!text) {
    return;
  }

  if (typeof window !== 'undefined' && window.isSecureContext && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      publicLinkCopied.value = true;
      setTimeout(() => publicLinkCopied.value = false, 1500);
      return;
    } catch {
      /* fallback below */
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);
  try {
    document.execCommand('copy');
    publicLinkCopied.value = true;
    setTimeout(() => publicLinkCopied.value = false, 1500);
  } finally {
    document.body.removeChild(textarea);
  }
};
</script>

<template>
  <AppLayout title="Edit Company">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        <span v-if="company.ID">Edit</span><span v-else>Create</span> Company: <b v-text="company.client"></b>
      </h2>
    </template>

    <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
      <FormSection :noGrid="true">
        <template #title>
          <a href="#" @click.prevent="goBack">
            <SecondaryButton>Back</SecondaryButton>
          </a>
        </template>
        <template #form v-if="company.ID">
          <!-- Profile Photo File Input -->
          <input ref="photoInput" type="file" class="hidden" @change="updatePhotoPreview">
          <img v-if="!photoPreview" :src="company.profile_photo_url" :alt="company.client"
            class="object-cover max-w-xs">

          <!-- New Profile Photo Preview -->
          <div v-if="photoPreview" class="mt-2">
            <span class="block bg-cover bg-no-repeat bg-center h-56"
              :style="'background-image: url(\'' + photoPreview + '\');'" />
          </div>

          <SecondaryButton class="mt-2" type="button" @click.prevent="selectNewPhoto">
            Select A New Photo
          </SecondaryButton>

          <SecondaryButton v-if="form.logo" type="button" class="mt-2" @click.prevent="deletePhoto">
            Remove Photo
          </SecondaryButton>

          <InputError :message="form.errors.photo" class="mt-2" />
        </template>
      </FormSection>
      <div v-if="publicCompanyUpdateEnabled && !publicCompanyUpdateUrl" class="mt-5 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        Enter the required fields from the customer call: Company Name, Company ID, Email, and Account Manager. After saving, this page will show a customer completion link that you can send to the customer so they can complete the remaining company details.
      </div>
      <div v-if="publicCompanyUpdateUrl" class="mt-5 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
        <p class="text-sm font-medium text-gray-800">Customer company completion link</p>
        <p class="text-xs text-gray-600 break-all font-mono">
          {{ publicCompanyUpdateUrl }}
        </p>
        <div class="flex flex-wrap items-center gap-2">
          <SecondaryButton type="button" @click="copyPublicCompanyUpdateLink">
            Copy link
          </SecondaryButton>
          <span v-if="publicLinkCopied" class="text-xs text-green-600">Copied!</span>
        </div>
      </div>
      <FormSection class="mt-5">
        <template #title>
          Company information
        </template>
        <template #description>
          These are the basic information for the company
        </template>
        <template #form>
          <!-- Company Name -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="client" value="Company Name" :required="true" />
            <TextInput id="client" v-model="form.client" type="text" class="mt-1 block w-full" autocomplete="client" />
            <InputError :message="form.errors.client" class="mt-2" />
          </div>
          <!-- Company Code -->
          <div class="col-span-6 sm:col-span-4 relative group">
            <InputLabel for="client_code" value="Company Code" />

            <TextInput id="client_code" v-model="form.client_code" type="text" class="mt-1 block w-full"
              autocomplete="off" :disabled="!helpers.userHasPermission(currentUser, 'admin')" />

            <div v-if="!helpers.userHasPermission(currentUser, 'admin')"
              class="absolute left-0 top-full mt-1 hidden group-hover:block bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg z-10">
              Only user with admin permissions can edit the company code.
            </div>

            <InputError :message="form.errors.client_code" class="mt-2" />
          </div>
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="active" value="Active" :required="true" />
            <SelectInput name="active"
              class="mt-1"
              id="active" v-model="form.active">
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </SelectInput>
            <InputError :message="form.errors.active" class="mt-2" />
          </div>
          <!-- Classes -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="class" value="Class" />
            <SelectInput name="class"
              class="mt-1"
              id="class" v-model="form.class">
              <option :value="null">Please select</option>
              <option v-for="companyClass in companyClasses" :value="companyClass"
                v-text="(companyClass === company.class) ? `${companyClass} (selected)` : companyClass"></option>
            </SelectInput>
            <InputError :message="form.errors.class" class="mt-2" />
          </div>
          <!-- Company ID -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="clientID" value="Company ID" :required="true" />
            <TextInput id="clientID" v-model="form.clientID" type="text" class="mt-1 block w-full"
              autocomplete="clientID" />
            <InputError :message="form.errors.clientID" class="mt-2" />
          </div>
          <!-- Account Manager -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="accountmanager" value="Account Manager" :required="true" />
            <SelectInput v-model="form.accountmanager"
              class="mt-1"
              name="accountmanager" id="accountmanager">
              <option v-for="manager in accountManagers" :value="manager.employeeID" v-text="helpers.fullName(manager)">
              </option>
            </SelectInput>
            <InputError :message="form.errors.accountmanager" class="mt-2" />
          </div>
          <!-- Shipper Number -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="shipperno" value="Shipper #" />
            <TextInput id="shipperno" v-model="form.shipperno" type="text" class="mt-1 block w-full"
              autocomplete="shipperno" />
            <InputError :message="form.errors.shipperno" class="mt-2" />
          </div>
          <!-- Carrier -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="carrier" value="Carrier" />
            <SelectInput v-model="form.carrier"
              class="mt-1"
              name="carrier" id="carrier">
              <option v-for="carrier in carriers" :value="carrier.carrier_id" v-text="carrier.carrier_name"></option>
            </SelectInput>
            <InputError :message="form.errors.carrier" class="mt-2" />
          </div>
          <!-- Intranet URL -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="intranet" value="Intranet URL" />
            <TextInput id="intranet" v-model="form.intranet" type="text" class="mt-1 block w-full"
              autocomplete="intranet" />
            <InputError :message="form.errors.intranet" class="mt-2" />
          </div>
          <div v-if="publicCompanyUpdateEnabled" class="col-span-6 sm:col-span-4">
            <InputLabel for="trucking_service" :value="truckingServiceLabel" />
            <TextInput id="trucking_service" v-model="form.trucking_service" type="text" class="mt-1 block w-full"
              autocomplete="off" />
            <InputError :message="form.errors.trucking_service" class="mt-2" />
          </div>
          <div v-if="publicCompanyUpdateEnabled" class="col-span-6 sm:col-span-4">
            <InputLabel for="insurance_carrier_policy_number" :value="insuranceCarrierPolicyNumberLabel" />
            <TextInput id="insurance_carrier_policy_number" v-model="form.insurance_carrier_policy_number" type="text"
              class="mt-1 block w-full" autocomplete="off" />
            <InputError :message="form.errors.insurance_carrier_policy_number" class="mt-2" />
          </div>
          <div v-if="publicCompanyUpdateEnabled" class="col-span-6">
            <CompanyLicenseDocumentField
              :existing-document="licenseDocumentState"
              :marked-for-removal="markedLicenseForRemoval"
              :license-document-error="form.errors.license_document"
              :remove-license-document-error="form.errors.remove_license_document"
              @file-selected="onLicenseFileSelected"
              @remove="onLicenseRemove"
              @undo-remove="onLicenseUndoRemove"
            />
          </div>
          <!-- Intranet URL -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="has_notifications" value="Email Notifications" />
            <SelectInput name="has_notifications"
              class="mt-1"
              id="has_notifications" v-model="form.has_notifications">
              <option :value="true">On</option>
              <option :value="false">Off</option>
            </SelectInput>
            <InputError :message="form.errors.has_notifications" class="mt-2" />
          </div>
          <!-- Notes -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="notes" value="Notes" />
            <textarea rows="4" id="notes" v-model="form.notes" type="text"
              class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full"
              autocomplete="notes"></textarea>
            <InputError :message="form.errors.notes" class="mt-2" />
          </div>
        </template>
      </FormSection>

      <FormSection class="mt-5">
        <template #title>
          Contact information
        </template>
        <template #description>
          How to reach out the company
        </template>
        <template #form>
          <!-- Email -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="email" value="Email" :required="true" />
            <TextInput id="email" v-model="form.email" type="email" class="mt-1 block w-full" />
            <InputError :message="form.errors.email" class="mt-2" />
          </div>
          <!-- Contact -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="contact" value="Contact" :required="contactIsRequired" />
            <TextInput id="contact" v-model="form.contact" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.contact" class="mt-2" />
          </div>
          <!-- URL -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="www" value="URL" />
            <TextInput id="www" v-model="form.www" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.www" class="mt-2" />
          </div>
          <!-- Phone -->
          <div class="col-span-6 sm:col-span-4">
            <PhoneInput v-model="form.phone" />
            <InputError :message="form.errors.phone" class="mt-2" />
          </div>
          <!-- Fax -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="fax" value="Fax" />
            <TextInput id="fax" v-model="form.fax" type="text" class="mt-1 block w-full" autocomplete="fax" />
            <InputError :message="form.errors.fax" class="mt-2" />
          </div>
          <!-- Address -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="address" :value="addressLabel" :required="props.publicCompanyUpdateEnabled" />
            <TextInput id="address" v-model="form.address" type="text" class="mt-1 block w-full"
              autocomplete="address" />
            <InputError :message="form.errors.address" class="mt-2" />
          </div>
          <!-- Address 2 -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="address2" :value="address2Label" />
            <TextInput id="address2" v-model="form.address2" type="text" class="mt-1 block w-full"
              autocomplete="address2" />
            <InputError :message="form.errors.address2" class="mt-2" />
          </div>
          <!-- City -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="city" value="City" />
            <TextInput id="city" v-model="form.city" type="text" class="mt-1 block w-full" autocomplete="city" />
            <InputError :message="form.errors.city" class="mt-2" />
          </div>
          <!-- State -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="state" :value="stateLabel" />
            <TextInput id="state" v-model="form.state" type="text" class="mt-1 block w-full" autocomplete="state" />
            <InputError :message="form.errors.state" class="mt-2" />
          </div>
          <!-- Zip -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="zip" value="Zip" />
            <TextInput id="zip" v-model="form.zip" type="text" class="mt-1 block w-full" autocomplete="zip" />
            <InputError :message="form.errors.zip" class="mt-2" />
          </div>
          <!-- Zip -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="country" value="Country" />
            <TextInput id="country" v-model="form.country" type="text" class="mt-1 block w-full"
              autocomplete="country" />
            <InputError :message="form.errors.country" class="mt-2" />
          </div>
        </template>
        <template #actions>
          <a href="#" @click.prevent="goBack" class="mr-5" v-if="!saving">
            <SecondaryButton type="button">Back</SecondaryButton>
          </a>
          <span class="text-green-500 mr-5" v-if="saved">Saved!</span>
          <PrimaryButton type="button" @click.prevent="saveForm" :disabled="saving">Save</PrimaryButton>
        </template>
      </FormSection>

      <FormSection class="mt-5" v-if="company.ID">
        <template #title>
          Company Contacts
        </template>
        <template #description>
          Users related to the company with the ability to login
        </template>
        <template #form>
          <div class="col-span-6">
            <p v-if="company.users.length === 0">No users related to this company
            </p>
            <div v-else>
              <p v-for="user in company.users"><span v-text="helpers.fullName(user)"></span>
                <span class="ml-2 font-semibold" v-text="`(${user.email})`"></span>
                <a :href="route('admin.users.edit', { user: user.employeeID })" class="ml-2" target="_blank"
                  ref="noopener noreferrer">
                  <SecondaryButton>Edit</SecondaryButton>
                </a>
              </p>
            </div>
            <a :href="route('admin.users.create', { userType: 2, employeeTypeId: company.ID })" target="_blank"
              ref="noopener noreferrer">
              <SecondaryButton>Create Company Contact</SecondaryButton>
            </a>
          </div>
        </template>
      </FormSection>
    </div>
  </AppLayout>
</template>
